import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, MessageCircle, Mail, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dateBR, dateTimeBR } from "../lib/format";
import { Card, CardHead, Pill } from "./common";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { STAGE, RemindActions } from "./AdmissionTracker";
import { AdmissionReviewDialog } from "./admission/AdmissionReviewDialog";
import { Milestones, PHASE_CLS, WHO } from "./admission/ReviewParts";

export const OCR_MSG = {
  lido: ["Lido pela IA", "text-emerald-700"],
  ilegivel: ["Não conseguimos ler. Envie outra foto com boa luz e sem reflexo.", "text-amber-700"],
  tipo_incorreto: ["Este arquivo não parece ser o documento pedido. Confira e envie de novo.", "text-rose-700"],
  falhou: ["Recebido. A leitura automática não está disponível agora; a equipe DP vai conferir.", "text-slate-500"],
  nao_suportado: ["Recebido. Este formato não é lido pela IA; a equipe DP vai conferir.", "text-slate-500"],
};
export const fmtDiv = (d, v) => (!v ? "—" : d.field === "birth_date" ? dateBR(v) : d.field === "cpf" && String(v).length === 11 ? `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}` : v);

const OCR_LABELS = { name: "Nome", cpf: "CPF", birth_date: "Nascimento", rg_number: "RG", doc_kind: "Tipo", holder_name: "Titular", street: "Rua", number: "Número", city: "Cidade", state: "UF", zip: "CEP", issue_date: "Emissão" };

function OcrPanel({ a }) {
  const ocr = a.ocr || {};
  if (!Object.keys(ocr).length) return null;
  const divs = a.ocr_divergences || [];
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="admission-ocr-panel">
      <div className="text-xs font-semibold text-slate-700">Leitura dos documentos por IA</div>
      {Object.entries(ocr).map(([k, o]) => (
        <div key={k} className="text-xs" data-testid={`admission-ocr-${k}`}>
          <span className="font-medium">{k === "identidade" ? "Identidade" : "Comprovante de residência"}:</span> <span className={OCR_MSG[o.status]?.[1]}>{o.status === "lido" ? "lido" : OCR_MSG[o.status]?.[0]}</span>
          {o.status === "lido" && <div className="text-slate-500 mt-0.5">{Object.entries(OCR_LABELS).filter(([f]) => o.fields?.[f]).map(([f, l]) => `${l}: ${o.fields[f]}`).join(" · ")}</div>}
        </div>))}
      {divs.length > 0 && <div className="bg-rose-50 border border-rose-200 rounded-md p-2 text-xs text-rose-800 space-y-0.5" data-testid="admission-ocr-divergences">
        {divs.map((d) => <div key={d.field}><b>{d.label}</b>: digitado {fmtDiv(d, d.typed)} · documento {fmtDiv(d, d.document)}</div>)}</div>}
    </div>
  );
}

export const ADM_STATUS = {
  concluido: ["Concluído", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  pendente: ["Pendente", "bg-slate-100 text-slate-600 border-slate-200"],
  com_divergencia: ["Com divergência", "bg-rose-50 text-rose-700 border-rose-200"],
  aguardando_empresa: ["Aguardando empresa", "bg-amber-50 text-amber-800 border-amber-300"],
  aguardando_funcionario: ["Aguardando funcionário", "bg-sky-50 text-sky-700 border-sky-200"],
  aguardando_dp: ["Aguardando DP", "bg-violet-50 text-violet-700 border-violet-200"],
  nao_aplicavel: ["Não aplicável", "bg-zinc-100 text-zinc-500 border-zinc-300"],
};

const PARTS = [["funcionario", "Cadastro do funcionário"], ["empresa", "Etapa da empresa"], ["dp", "Processamento DPdigi"]];
const ProgressParts = ({ p }) => (
  <div className="grid gap-2 sm:grid-cols-3" data-testid="admission-progress-parts">
    {PARTS.map(([k, l]) => <div key={k} className="rounded-lg border px-3 py-2"><div className="flex justify-between text-[11px] text-slate-500"><span>{l}</span><span className="font-semibold text-slate-700" data-testid={`admission-progress-${k}`}>{p?.[k] ?? 0}%</span></div>
      <Progress value={p?.[k] ?? 0} className="mt-1 h-1.5" /></div>)}
  </div>);
const Remaining = ({ a }) => {
  const left = a.checklist.filter((c) => !["concluido", "nao_aplicavel"].includes(c.status));
  if (!left.length) return null;
  return <p className="text-xs text-slate-600" data-testid="admission-remaining"><b>Falta para 100%:</b> {left.map((c) => `${c.label} (${WHO[c.owner]})`).join(", ")}. Itens do DPdigi são feitos após o encaminhamento e não impedem a validação da empresa.</p>;
};

export const admissionLink = (token) => `${window.location.origin}/admissao/${token}`;
// WhatsApp invite: same token, entry URL with a neutral link preview that redirects to admissionLink.
export const inviteLink = (token) => `${window.location.origin}/api/public/convite/${token}`;

export function ShareLink({ token, name }) {
  const url = admissionLink(token);
  const msg = `Olá${name ? `, ${name.split(" ")[0]}` : ""}! Para concluir sua admissão, preencha seus dados e envie seus documentos pelo celular neste link seguro: ${url}`;
  const waMsg = msg.replace(url, inviteLink(token));
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiado"); } catch { toast.error("Não foi possível copiar."); } };
  return (
    <div className="space-y-2" data-testid="admission-share">
      <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-2"><Link2 className="h-4 w-4 text-indigo-600 shrink-0" /><span data-testid="admission-link-text" className="font-mono text-[11px] truncate">{url}</span></div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" data-testid="admission-copy-link" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1.5" />Copiar link</Button>
        <Button size="sm" variant="outline" data-testid="admission-share-whatsapp" asChild><a href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</a></Button>
        <Button size="sm" variant="outline" data-testid="admission-share-email" asChild><a href={`mailto:?subject=${encodeURIComponent("Sua admissão digital")}&body=${encodeURIComponent(msg)}`}><Mail className="h-3.5 w-3.5 mr-1.5" />E-mail</a></Button>
      </div>
    </div>
  );
}

export function AdmissionCard({ employee }) {
  const { can } = useAuth();
  const [a, setA] = useState(undefined);
  const [rev, setRev] = useState(false);
  const load = () => api.get(`/admissions/by-employee/${employee.id}`).then((r) => setA(r.data)).catch(() => setA(null));
  useEffect(() => { load(); }, [employee.id]); // eslint-disable-line
  const generate = async () => { try { const { data } = await api.post(`/admissions/by-employee/${employee.id}`); setA(data); toast.success("Link seguro gerado (válido por 15 dias)"); } catch (e) { toast.error(errMsg(e)); } };
  const setItem = async (key, status) => { try { const { data } = await api.patch(`/admissions/by-employee/${employee.id}/checklist/${key}`, { status }); setA(data); } catch (e) { toast.error(errMsg(e)); } };
  if (a === undefined) return null;
  if (employee.status !== "pre_admissao") {
    if (a?.phase !== "concluida") return null;
    return <Card testid="admission-card"><div className="flex items-center gap-2 p-4 text-sm font-medium text-emerald-800" data-testid="admission-completed">
      <CheckCircle2 className="h-5 w-5 text-emerald-600" />Admissão concluída com sucesso.{a.completed ? ` Concluída pela equipe DPdigi (${a.completed.by}) em ${dateTimeBR(a.completed.at)}.` : ""}</div></Card>;
  }
  return (
    <Card testid="admission-card">
      <CardHead title="Admissão digital" hint={a ? `Status: ${a.phase_label || "aguardando candidato"} · link válido até ${dateBR(a.expires_at)}` : "Envie um link seguro para o candidato preencher tudo pelo celular"}
        right={can("requests.create") && <Button size="sm" variant={a ? "outline" : "default"} data-testid="admission-generate-link" onClick={generate}><RefreshCw className="h-3.5 w-3.5 mr-1.5" />{a ? "Gerar novo link" : "Gerar link"}</Button>} />
      {a && (
        <div className="px-5 pb-5 space-y-4">
          <div className={`flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center ${a.phase === "aguardando_empresa" ? "border-amber-300 bg-amber-50/60" : "bg-slate-50/60"}`} data-testid="admission-phase-box">
            <div className="min-w-0 flex-1 space-y-1.5"><div className="flex flex-wrap items-center gap-2"><Pill cls={PHASE_CLS[a.phase]} testid="admission-phase">{a.phase_label}</Pill>
              {a.actor && <span className="text-xs text-slate-500" data-testid="admission-actor">Próximo passo: {WHO[a.actor]}</span>}</div><Milestones phase={a.phase} /></div>
            <Button size="sm" className="shrink-0" onClick={() => setRev(true)} data-testid="admission-review-open">Revisar e finalizar admissão</Button>
          </div>
          <div className="flex items-center gap-3"><span className="text-[11px] text-slate-500">Progresso geral</span><Progress value={a.progress} className="h-2 flex-1" /><span className="font-display font-semibold" data-testid="admission-progress">{a.progress}%</span></div>
          <ProgressParts p={a.progress_parts} />
          <Remaining a={a} />
          <AdmissionReviewDialog employeeId={employee.id} open={rev} onOpenChange={setRev} onChanged={load} />
          {a.stage && a.stage !== "enviado" && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border rounded-lg px-3 py-2.5 bg-slate-50/60" data-testid="admission-stage-box">
              <div className="text-xs text-slate-600"><Pill cls={STAGE[a.stage][1]} testid="admission-stage">{STAGE[a.stage][0]}</Pill>
                <span className="ml-2">{(a.reminders || []).length ? `${a.reminders.length} lembrete(s) enviado(s)` : "Nenhum lembrete enviado"}</span></div>
              {can("requests.create") && <RemindActions item={{ employee_id: employee.id, name: employee.name, email: employee.email, phone: employee.phone, token: a.token }} onDone={load} />}
            </div>
          )}
          <ShareLink token={a.token} name={employee.name} />
          <div className="grid sm:grid-cols-2 gap-2">
            {a.checklist.map((c) => (
              <div key={c.key} data-testid={`admission-item-${c.key}`} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2">
                <span className="text-sm">{c.label}</span>
                {c.owner !== "funcionario" && (c.owner === "empresa" || can("backoffice.process")) ? (
                  <Select value={c.status} onValueChange={(v) => setItem(c.key, v)}>
                    <SelectTrigger data-testid={`admission-item-select-${c.key}`} className="h-7 w-[170px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ADM_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Pill cls={ADM_STATUS[c.status]?.[1]}>{ADM_STATUS[c.status]?.[0]}</Pill>}
              </div>
            ))}
          </div>
          <OcrPanel a={a} />
          <p className="text-[11px] text-slate-500">O item Assinatura é atualizado automaticamente pelo cartão de contrato abaixo. A leitura por IA ajuda na conferência, mas não substitui o documento original. Divergências nunca sobrescrevem dados informados pela empresa.</p>
        </div>
      )}
    </Card>
  );
}
